/** @param {{ lat?: number, lng?: number } | null | undefined} coords */
export function isValidCoordinates(coords) {
  const lat = Number(coords?.lat);
  const lng = Number(coords?.lng);
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/** PostGIS geography(Point,4326) WKT for Supabase insert */
export function formatGeographyPoint(coords) {
  if (!isValidCoordinates(coords)) return null;
  const lat = Number(coords.lat);
  const lng = Number(coords.lng);
  return `SRID=4326;POINT(${lng} ${lat})`;
}

/** Haversine great-circle distance in km (WGS84). */
export function haversineDistanceKm(lat1, lng1, lat2, lng2) {
  const aLat = Number(lat1);
  const aLng = Number(lng1);
  const bLat = Number(lat2);
  const bLng = Number(lng2);
  if (![aLat, aLng, bLat, bLng].every(Number.isFinite)) return Infinity;
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const sinHalf =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(sinHalf), Math.sqrt(1 - sinHalf));
}

/** @param {number|null|undefined} meters */
export function formatDistanceMeters(meters) {
  const m = Number(meters);
  if (!Number.isFinite(m)) return '';
  if (m < 1000) return `${Math.round(m)}m`;
  return `${(m / 1000).toFixed(1)}km`;
}

/** Hangul syllable/jamo ranges — used to route Korean-looking queries to Kakao Local. */
function containsHangul(text) {
  return /[가-힣ᄀ-ᇿ㄰-㆏]/.test(String(text || ''));
}

/** Rough Korea bounding box — used to route reverse-geocode calls to Kakao Local. */
function isInsideKorea(lat, lng) {
  const la = Number(lat);
  const ln = Number(lng);
  return Number.isFinite(la) && Number.isFinite(ln) && la >= 33 && la <= 39.5 && ln >= 124 && ln <= 132;
}

const KAKAO_REST_API_KEY = process.env.REACT_APP_KAKAO_REST_API_KEY || '';
const GOONG_API_KEY = process.env.REACT_APP_GOONG_API_KEY || '';
const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

/**
 * Kakao Local — forward address search (returns lat/lng directly, no separate detail call needed).
 * @param {string} query
 * @param {number} limit
 * @returns {Promise<{lat:number,lng:number,displayName:string}[]>}
 */
async function searchAddressCandidatesKakao(query, limit = 5) {
  const q = String(query || '').trim();
  if (!q || !KAKAO_REST_API_KEY) return [];
  const headers = { Authorization: `KakaoAK ${KAKAO_REST_API_KEY}` };
  const size = Math.max(1, Math.min(15, limit));

  // 주소(도로명/지번) 검색 + 키워드(건물·아파트 단지명 등 POI) 검색을 함께 호출해서 합침.
  // "address"만 쓰면 도로명 자체는 나와도 "OO아파트" 같은 건물명은 안 걸림 — 키워드 검색이 그걸 커버함.
  const [addrRes, keywordRes] = await Promise.all([
    fetch(`https://dapi.kakao.com/v2/local/search/address.json?query=${encodeURIComponent(q)}&size=${size}`, { headers }).catch(() => null),
    fetch(`https://dapi.kakao.com/v2/local/search/keyword.json?query=${encodeURIComponent(q)}&size=${size}`, { headers }).catch(() => null),
  ]);

  const out = [];
  const seen = new Set();
  const pushRow = (lat, lng, displayName) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !displayName) return;
    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ lat, lng, displayName });
  };

  // 키워드(건물명 포함) 결과를 우선 배치
  if (keywordRes?.ok) {
    try {
      const data = await keywordRes.json();
      for (const d of Array.isArray(data?.documents) ? data.documents : []) {
        const lat = Number(d.y);
        const lng = Number(d.x);
        const addr = d.road_address_name || d.address_name || '';
        const displayName = d.place_name ? `${d.place_name} (${addr})` : addr;
        pushRow(lat, lng, displayName || q);
      }
    } catch {
      // ignore
    }
  }

  // 순수 주소(도로명/지번) 결과로 나머지 채움
  if (addrRes?.ok) {
    try {
      const data = await addrRes.json();
      for (const d of Array.isArray(data?.documents) ? data.documents : []) {
        const lat = Number(d.y);
        const lng = Number(d.x);
        const displayName = d.road_address?.address_name || d.address?.address_name || d.address_name || q;
        pushRow(lat, lng, displayName);
      }
    } catch {
      // ignore
    }
  }

  return out.slice(0, size);
}

/**
 * Kakao Local — reverse geocode (coord → address).
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<{displayName:string,lat:number,lng:number}|null>}
 */
async function reverseGeocodeCoordsKakao(lat, lng) {
  const latN = Number(lat);
  const lngN = Number(lng);
  if (!Number.isFinite(latN) || !Number.isFinite(lngN) || !KAKAO_REST_API_KEY) return null;
  try {
    const url = `https://dapi.kakao.com/v2/local/geo/coord2address.json?x=${lngN}&y=${latN}`;
    const res = await fetch(url, { headers: { Authorization: `KakaoAK ${KAKAO_REST_API_KEY}` } });
    if (!res.ok) return null;
    const data = await res.json();
    const doc = Array.isArray(data?.documents) ? data.documents[0] : null;
    const displayName = doc?.road_address?.address_name || doc?.address?.address_name;
    if (!displayName) return null;
    return { displayName, lat: latN, lng: lngN };
  } catch {
    return null;
  }
}

/**
 * Goong.io — Autocomplete + Place Detail combined (VN address search).
 * NOTE: needs REACT_APP_GOONG_API_KEY to activate; falls back to [] (caller falls through to Nominatim) until set.
 * @param {string} query
 * @param {number} limit
 * @returns {Promise<{lat:number,lng:number,displayName:string}[]>}
 */
async function searchAddressCandidatesGoong(query, limit = 5) {
  const q = String(query || '').trim();
  if (!q || !GOONG_API_KEY) return [];
  try {
    const acUrl = `https://rsapi.goong.io/Place/AutoComplete?api_key=${encodeURIComponent(GOONG_API_KEY)}&input=${encodeURIComponent(q)}`;
    const acRes = await fetch(acUrl);
    if (!acRes.ok) return [];
    const acData = await acRes.json();
    const predictions = (Array.isArray(acData?.predictions) ? acData.predictions : []).slice(0, Math.max(1, Math.min(10, limit)));
    if (!predictions.length) return [];

    const detailResults = await Promise.all(
      predictions.map(async (p) => {
        if (!p?.place_id) return null;
        try {
          const detUrl = `https://rsapi.goong.io/Place/Detail?place_id=${encodeURIComponent(p.place_id)}&api_key=${encodeURIComponent(GOONG_API_KEY)}`;
          const detRes = await fetch(detUrl);
          if (!detRes.ok) return null;
          const detData = await detRes.json();
          const loc = detData?.result?.geometry?.location;
          const lat = Number(loc?.lat);
          const lng = Number(loc?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return { lat, lng, displayName: detData?.result?.formatted_address || p.description || q };
        } catch {
          return null;
        }
      }),
    );
    return detailResults.filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Google Places API (New) — 세션 토큰 관리.
 * 검색어를 치는 동안(Autocomplete) 계속 같은 토큰을 재사용하다가, 사용자가 후보를 하나 "선택"해서
 * Place Details를 호출하는 순간 그 세션이 끝나고 과금 1건으로 묶인다. Details 호출 후에는 반드시
 * 새 토큰을 발급해야 다음 검색이 별도 세션으로 잡힌다 (안 그러면 세션이 무한정 이어져서
 * 구글이 세션 과금이 아닌 건별 과금으로 처리할 수 있음).
 */
let googlePlacesSessionToken = null;
function getGooglePlacesSessionToken() {
  if (!googlePlacesSessionToken) {
    googlePlacesSessionToken =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `mingo-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
  return googlePlacesSessionToken;
}
function resetGooglePlacesSessionToken() {
  googlePlacesSessionToken = null;
}

/**
 * Google Places API (New) — Autocomplete.
 * 좌표는 여기서 안 옴(New API 스펙) — placeId만 받아두고, 사용자가 후보를 실제로 선택할 때만
 * resolveCandidateCoords()가 Place Details를 호출해서 좌표를 채운다. 이렇게 해야 검색 후보
 * 5개를 다 Details 호출하지 않고 "선택된 1개"만 과금돼서 세션 가격 혜택을 그대로 받는다.
 * @param {string} query
 * @param {number} limit
 * @returns {Promise<{lat:null,lng:null,displayName:string,placeId:string,provider:'google'}[]>}
 */
async function searchAddressCandidatesGoogle(query, limit = 5) {
  const q = String(query || '').trim();
  if (!q || !GOOGLE_PLACES_API_KEY) return [];
  try {
    const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
      },
      body: JSON.stringify({
        input: q,
        sessionToken: getGooglePlacesSessionToken(),
        languageCode: 'vi',
        regionCode: 'VN',
        // 국가 강제 제한(하드) — 이게 있어야 아예 다른 나라로 튀는 사고를 원천 차단함.
        // locationBias는 그 안에서 도시 단위 우선순위만 주는 소프트 힌트.
        includedRegionCodes: ['vn'],
        locationBias: {
          rectangle: {
            low: { latitude: 15.95, longitude: 108.05 },
            high: { latitude: 16.15, longitude: 108.3 },
          },
        },
      }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
    return suggestions
      .map((s) => {
        const pred = s?.placePrediction;
        const placeId = pred?.placeId;
        const displayName = pred?.text?.text;
        if (!placeId || !displayName) return null;
        return { lat: null, lng: null, displayName, placeId, provider: 'google' };
      })
      .filter(Boolean)
      .slice(0, Math.max(1, Math.min(10, limit)));
  } catch {
    return [];
  }
}

/**
 * Google Geocoding API — 번지수(예: "74 Ha Ky Ngo")처럼 구체적인 주소 문자열을 그대로 지오코딩.
 * Autocomplete는 구글 DB에 "장소"로 개별 등록된 것만 후보로 내놓기 때문에 베트남 골목 안
 * 개별 집 번지수는 데이터가 없으면 아예 안 뜬다. Geocoding은 그 도로의 알려진 다른 번지들
 * 사이 위치를 보간(interpolation)해서 등록 안 된 번지도 대략적인 정확한 지점을 찾아준다 —
 * 세션 개념이 없는 건별 과금이라 Details 호출도 필요 없이 한 번에 좌표까지 나온다.
 * @param {string} query
 * @param {number} limit
 * @returns {Promise<{lat:number,lng:number,displayName:string}[]>}
 */
async function geocodeAddressGoogle(query, limit = 3) {
  const q = String(query || '').trim();
  if (!q || !GOOGLE_PLACES_API_KEY) return [];
  try {
    // 다낭 지역을 우선 매칭하도록 바이어스 — 안 그러면 "Ho Thau" 같은 짧은 지명이
    // 하장성(Hà Giang, 완전히 다른 북부 지역)의 동명 지명으로 잘못 매칭되는 경우가 있음.
    // bounds는 강제 제한이 아니라 우선순위 힌트라, 다낭 밖 주소도 여전히 찾을 수 있음.
    const DANANG_BOUNDS = '15.95,108.05|16.15,108.30';
    // components=country:VN — 국가 강제 제한(하드). region/bounds는 그 안에서의 소프트 힌트.
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(q)}&region=vn&bounds=${DANANG_BOUNDS}&components=country:VN&key=${GOOGLE_PLACES_API_KEY}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    if (data?.status !== 'OK' || !Array.isArray(data?.results)) return [];
    return data.results
      .slice(0, Math.max(1, Math.min(10, limit)))
      .map((r) => {
        const lat = Number(r?.geometry?.location?.lat);
        const lng = Number(r?.geometry?.location?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng, displayName: r.formatted_address || q };
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Google Places API (New) — Place Details. 세션을 끝내는 호출이라 여기서 토큰을 리셋한다.
 * @param {string} placeId
 * @returns {Promise<{lat:number,lng:number,displayName:string}|null>}
 */
async function resolvePlaceDetailsGoogle(placeId) {
  if (!placeId || !GOOGLE_PLACES_API_KEY) return null;
  const token = getGooglePlacesSessionToken();
  resetGooglePlacesSessionToken(); // 다음 검색은 새 세션으로 — Details 호출로 이 세션은 끝남
  try {
    const url = `https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}?sessionToken=${encodeURIComponent(token)}`;
    const res = await fetch(url, {
      headers: {
        'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
        'X-Goog-FieldMask': 'formattedAddress,location',
      },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const lat = Number(data?.location?.latitude);
    const lng = Number(data?.location?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng, displayName: data?.formattedAddress || '' };
  } catch {
    return null;
  }
}

/**
 * 검색 결과 후보를 "선택"했을 때 호출. 이미 좌표가 있는 후보(카카오/구웅/노미나팀)는 그대로 반환하고,
 * 구글 Autocomplete 후보(placeId만 있고 좌표는 null)만 이 시점에 Place Details를 호출해 좌표를 채운다.
 * @param {{lat:number|null,lng:number|null,displayName:string,placeId?:string,provider?:string}} candidate
 * @returns {Promise<{lat:number,lng:number,displayName:string}|null>}
 */
export async function resolveCandidateCoords(candidate) {
  if (!candidate) return null;
  if (isValidCoordinates(candidate)) return candidate;
  if (candidate.provider === 'google' && candidate.placeId) {
    const detail = await resolvePlaceDetailsGoogle(candidate.placeId);
    if (!detail) return null;
    return { lat: detail.lat, lng: detail.lng, displayName: detail.displayName || candidate.displayName };
  }
  return null;
}

/**
 * Free forward-geocoding via OpenStreetMap Nominatim (no API key required).
 * @param {string} query free-text address
 * @returns {Promise<{lat:number,lng:number,displayName:string}|null>}
 */
export async function geocodeAddress(query) {
  const q = String(query || '').trim();
  if (!q) return null;
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const rows = await res.json();
    const hit = Array.isArray(rows) ? rows[0] : null;
    if (!hit) return null;
    const lat = Number(hit.lat);
    const lng = Number(hit.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng, displayName: hit.display_name || q };
  } catch {
    return null;
  }
}

/**
 * Free forward-geocoding via Nominatim, returning multiple candidates so the user can
 * pick the right one (used by the standalone address-book search — separate from the
 * single-best-match geocodeAddress() used for delivery-fee estimation).
 * @param {string} query
 * @param {number} [limit]
 * @returns {Promise<{lat:number,lng:number,displayName:string}[]>}
 */
export async function searchAddressCandidates(query, limit = 5) {
  const q = String(query || '').trim();
  if (!q) return [];

  // 한글 검색어 → 카카오 로컬 (한국 주소 데이터가 훨씬 정밀함)
  if (containsHangul(q)) {
    const kakaoRows = await searchAddressCandidatesKakao(q, limit);
    if (kakaoRows.length) return kakaoRows;
    // 카카오에서 못 찾으면(예: 키 미설정) Nominatim으로 폴백 — 아래로 계속 진행
  } else if (GOOGLE_PLACES_API_KEY) {
    // "74 Ha Ky Ngo"처럼 번지수로 시작하는 구체적 주소는 Geocoding으로 먼저 시도 —
    // Autocomplete는 구글이 개별 "장소"로 등록해둔 것만 후보로 주기 때문에 이런 골목 안
    // 개별 번지수는 못 찾는 경우가 많고, Geocoding은 보간으로 그 번지수까지 찾아준다.
    if (/^\d/.test(q)) {
      const geocodeRows = await geocodeAddressGoogle(q, limit);
      if (geocodeRows.length) return geocodeRows;
    }
    // 구글 키가 설정돼 있으면 베트남 등 그 외 지역은 구글로 (세션 과금이라 저렴, 정밀도도 좋음)
    const googleRows = await searchAddressCandidatesGoogle(q, limit);
    if (googleRows.length) return googleRows;
  } else if (GOONG_API_KEY) {
    // Goong 키가 설정돼 있으면 베트남 주소는 Goong으로 (구글 키 없을 때의 대체 경로)
    const goongRows = await searchAddressCandidatesGoong(q, limit);
    if (goongRows.length) return goongRows;
  }

  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=${Math.max(1, Math.min(10, limit))}&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const rows = await res.json();
    if (!Array.isArray(rows)) return [];
    return rows
      .map((hit) => {
        const lat = Number(hit.lat);
        const lng = Number(hit.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng, displayName: hit.display_name || q };
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Google Geocoding API — 역지오코딩(좌표 → 주소). 지도 핀 드래그로 위치를 조정했을 때
 * 그 지점의 주소 텍스트를 다시 얻어오는 데 씀. 세션 개념 없는 건별 과금.
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<{displayName:string,lat:number,lng:number}|null>}
 */
async function reverseGeocodeCoordsGoogle(lat, lng) {
  const latN = Number(lat);
  const lngN = Number(lng);
  if (!Number.isFinite(latN) || !Number.isFinite(lngN) || !GOOGLE_PLACES_API_KEY) return null;
  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latN},${lngN}&language=vi&key=${GOOGLE_PLACES_API_KEY}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const first = Array.isArray(data?.results) ? data.results[0] : null;
    if (data?.status !== 'OK' || !first?.formatted_address) return null;
    return { displayName: first.formatted_address, lat: latN, lng: lngN };
  } catch {
    return null;
  }
}

/**
 * Free reverse-geocoding via OpenStreetMap Nominatim.
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<{displayName:string,lat:number,lng:number}|null>}
 */
export async function reverseGeocodeCoords(lat, lng) {
  const latN = Number(lat);
  const lngN = Number(lng);
  if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return null;

  if (isInsideKorea(latN, lngN)) {
    const kakaoResult = await reverseGeocodeCoordsKakao(latN, lngN);
    if (kakaoResult) return kakaoResult;
  } else if (GOOGLE_PLACES_API_KEY) {
    const googleResult = await reverseGeocodeCoordsGoogle(latN, lngN);
    if (googleResult) return googleResult;
  }

  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latN}&lon=${lngN}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.display_name) return null;
    return { displayName: data.display_name, lat: latN, lng: lngN };
  } catch {
    return null;
  }
}
